import { useSearchParams } from 'react-router';
import { DataLoader } from '@/components/ui/DataLoader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CarryProfileCard } from '@/portal/carry/components/CarryProfileCard';
import { InvestorEntitiesTab } from '@/portal/profile/components/InvestorEntitiesTab';
import { PersonalProfileTab } from '@/portal/profile/components/PersonalProfileTab';
import { useMe } from '@/portal/shared/hooks/useMe';
import type { PortalMe } from '@/portal/shared/services/meService';

export function PortalProfilePage() {
  const { data, isLoading, error } = useMe();

  return (
    <DataLoader<PortalMe> data={data} isLoading={isLoading} error={error}>
      {(me) => <ProfileTabs me={me} />}
    </DataLoader>
  );
}

function ProfileTabs({ me }: { me: PortalMe }) {
  const hasInvestorSide = me.investor != null;
  const carryProfile = me.accessible_modules.includes('fund_carry')
    ? me.fund_member
    : null;
  const hasCarrySide = carryProfile != null;
  const defaultTab = hasInvestorSide ? 'entities' : 'carry';
  const tabCount = (hasInvestorSide ? 2 : 0) + (hasCarrySide ? 1 : 0);
  const availableTabs = [
    ...(hasInvestorSide ? ['entities', 'personal'] : []),
    ...(hasCarrySide ? ['carry'] : []),
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab && availableTabs.includes(requestedTab)
      ? requestedTab
      : defaultTab;

  const handleTabChange = (newTab: string) => {
    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.set('tab', newTab);
      return nextParams;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Profile</h1>
        <p className="text-muted-foreground mt-1">
          Manage your personal information
          {hasInvestorSide && ' and investing entities'}
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList
          className="grid w-full"
          style={{ gridTemplateColumns: `repeat(${tabCount}, minmax(0, 1fr))` }}
        >
          {hasInvestorSide && (
            <TabsTrigger value="entities">Entities</TabsTrigger>
          )}
          {hasInvestorSide && (
            <TabsTrigger value="personal">Personal</TabsTrigger>
          )}
          {hasCarrySide && <TabsTrigger value="carry">Carry</TabsTrigger>}
        </TabsList>

        {hasInvestorSide && (
          <TabsContent value="entities" className="space-y-4">
            <InvestorEntitiesTab />
          </TabsContent>
        )}

        {hasInvestorSide && (
          <TabsContent value="personal" className="space-y-4">
            <PersonalProfileTab />
          </TabsContent>
        )}

        {carryProfile && (
          <TabsContent value="carry" className="space-y-4">
            <CarryProfileCard profile={carryProfile} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
